import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Video, History as HistoryIcon, AlertTriangle, CheckCircle2, Database, Gauge, Share2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PitchMechanicsCapture from "@/components/PitchMechanicsCapture";
import PitchTelemetryImporter from "@/components/PitchTelemetryImporter";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * PitchMechanicsPage
 * ──────────────────
 * Standalone capture page reachable any time from the player portal or the
 * coach's player-detail screen. NOT gated behind onboarding — athletes and
 * coaches can re-capture a delivery whenever they need a new kinematic
 * snapshot (e.g. after a mechanics tweak, post-recovery, mid-season).
 *
 * Query params:
 *   ?playerId=<user_id> — coach administering for a roster athlete
 *   ?handedness=right|left — override the default arm (defaults to athlete
 *                            profile's gird_dominant_arm, falls back to "right")
 */
const NORM_LABELS = {
  peak_ext_rotation_deg: "Peak external rotation",
  stride_length_pct: "Stride length",
  trunk_tilt_release_deg: "Trunk lean @ release",
  front_knee_flex_br_deg: "Front-knee flex @ release",
  shoulder_abduction_deg: "Shoulder abduction",
  hip_shoulder_separation_deg: "Hip-shoulder separation",
  head_tilt_max_deg: "Head lateral tilt (15° rule)",
  head_drift_in: "Head lateral drift",
};

export default function PitchMechanicsPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const playerIdParam = params.get("playerId") || null;

  const [handedness, setHandedness] = useState(params.get("handedness") || "right");
  const [latest, setLatest] = useState(null);
  const [norms, setNorms] = useState({});
  const [telemetry, setTelemetry] = useState([]);
  const [recording, setRecording] = useState(false);

  const loadLatest = async () => {
    try {
      const { data } = await api.get("/screening/pitch-mechanics/latest");
      setLatest(data?.capture || null);
      setNorms(data?.norms || {});
      setTelemetry(data?.telemetry || []);
      const lastHand = data?.capture?.data?.handedness;
      if (lastHand) setHandedness(lastHand);
    } catch {/* first-time empty state */}
  };

  useEffect(() => { loadLatest(); }, []);

  const onComplete = async (metrics) => {
    try {
      const payload = { ...metrics };
      if (playerIdParam) payload.player_user_id = playerIdParam;
      const { data } = await api.post("/screening/pitch-mechanics", payload);
      if (data.flag_count > 0) {
        toast.warning(`${data.flag_count} kinematic${data.flag_count === 1 ? "" : "s"} outside the elite window — review the Why pane.`);
      } else {
        toast.success("Pitch mechanics saved · all kinematics within elite window.");
      }
      await loadLatest();
      setRecording(false);
    } catch (e) {
      console.error(e);
      toast.error("Could not save pitch mechanics — try recording again.");
    }
  };

  const isCoach = user?.role === "coach" || user?.role === "admin";
  const portalHome = isCoach ? "/dashboard" : "/portal";
  const cameraLabel = handedness === "right" ? "3rd-base side (RHP)" : "1st-base side (LHP)";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="pitch-mechanics-page">
      {/* HEADER */}
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">Pitch Mechanics</span>
        </div>
        <Link to={portalHome} className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1" data-testid="pitch-back-portal">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </Link>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-16 space-y-6">
        {/* HERO */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.25em] uppercase border"
               style={{ backgroundColor: "rgba(217,4,41,0.12)", borderColor: "rgba(217,4,41,0.35)", color: "var(--brand-secondary)" }}>
            <Video className="h-3 w-3" /> 2D + 3D Markerless Capture
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mt-3 tracking-tighter">
            Capture a delivery, anytime.
          </h1>
          <p className="text-sm text-white/65 mt-3 leading-relaxed">
            Film one mound pitch from the proper camera side and ArmIQ extracts peak external rotation, stride length, trunk lean, front-knee block, shoulder abduction, and hip-shoulder separation —
            with a 2D skeleton overlay, a rotatable 3D wireframe, and a stylized 3D mound scene to scrub frame-by-frame. Re-capture whenever you tweak mechanics.
          </p>
        </div>

        {/* HANDEDNESS / CAMERA SIDE */}
        {!recording && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="pitch-pre-card">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Step 1 · Pick the throwing arm</div>
            <div className="grid grid-cols-2 gap-3">
              {["right", "left"].map((arm) => (
                <button key={arm} onClick={() => setHandedness(arm)} data-testid={`pitch-arm-${arm}`}
                  className={`rounded-lg border px-4 py-5 transition-all text-left
                              ${handedness === arm ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                  <div className="text-white font-bold text-lg capitalize">{arm}-handed pitcher</div>
                  <div className="text-[10px] text-white/55 mt-1 uppercase tracking-wide">
                    Camera: {arm === "right" ? "3rd-base side" : "1st-base side"}
                  </div>
                </button>
              ))}
            </div>
            <div className="rounded-lg p-3 mt-4 border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-100" data-testid="pitch-camera-instructions">
              <div className="font-bold text-cyan-200 mb-1">Camera setup · {cameraLabel}</div>
              Mount on a tripod ~15-20 ft from the mound on the <b className="text-white">{handedness === "right" ? "3rd-base" : "1st-base"} side</b>.
              Lens at hip height, frame the whole body head-to-foot.
            </div>
            <Button onClick={() => setRecording(true)} className="w-full mt-4"
                    style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="pitch-start-capture">
              ▶ Start capture
            </Button>
          </Card>
        )}

        {/* CAPTURE */}
        {recording && (
          <PitchMechanicsCapture key={handedness}
                                 handedness={handedness}
                                 onComplete={onComplete}
                                 onCancel={() => setRecording(false)} />
        )}

        {/* LATEST CAPTURE — Compact summary */}
        {latest && !recording && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="pitch-latest-card">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Most recent capture</div>
                <div className="text-lg font-display font-black text-white mt-1">
                  {new Date(latest.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <HistoryIcon className="h-5 w-5 text-white/40" />
                <Button size="sm" variant="outline"
                        className="border-cyan-400/40 text-cyan-200 bg-cyan-500/5 hover:bg-cyan-500/10"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/screening/pitch-mechanics/${latest.event_id}/share`);
                            const url = `${window.location.origin}${data.share_path}`;
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success("Share link copied to clipboard.");
                            } catch {
                              toast.success(`Share link ready: ${url}`);
                            }
                          } catch (e) {
                            toast.error("Could not mint share link.");
                          }
                        }}
                        data-testid="pitch-share-link">
                  <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share recruiter card
                </Button>
                <Button size="sm" onClick={() => setRecording(true)}
                        style={{ backgroundColor: "var(--brand-secondary)" }}
                        data-testid="pitch-recapture">
                  <Video className="h-3.5 w-3.5 mr-1.5" /> Re-capture
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(NORM_LABELS).map(([k, label]) => {
                const val = latest.data?.[k];
                const norm = norms[k];
                const outOfBand = val != null && norm && (val < norm.low || val > norm.high);
                if (val == null) return null;
                return (
                  <div key={k} className="rounded p-3 bg-black/30 border border-white/5" data-testid={`pitch-metric-${k}`}>
                    <div className="text-[10px] text-white/55 uppercase tracking-wider">{label}</div>
                    <div className="flex items-baseline gap-1.5 mt-0.5">
                      <span className="font-display font-bold text-lg text-white">{val}{k.endsWith("_pct") ? "%" : k.endsWith("_in") ? "″" : "°"}</span>
                      {outOfBand && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />}
                      {!outOfBand && norm && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                    </div>
                    {norm && <div className="text-[10px] text-white/40 mt-0.5">Norm {norm.low}-{norm.high}</div>}
                  </div>
                );
              })}
            </div>
            {Array.isArray(latest.data?.flags) && latest.data.flags.length > 0 && (
              <div className="mt-4 rounded-lg p-3 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200" data-testid="pitch-flags">
                <div className="font-bold text-amber-200 mb-1">
                  {latest.data.flags.length} kinematic{latest.data.flags.length === 1 ? "" : "s"} outside the elite window
                </div>
                Review the Intelligence Engine&apos;s Why pane on your portal — corrective drills may have been auto-seeded for tomorrow.
              </div>
            )}

            {/* Linked vendor telemetry — pitch-by-pitch fusion */}
            <TelemetryPanel telemetry={telemetry} />
          </Card>
        )}

        {/* Vendor telemetry importer — attaches to the latest capture */}
        {latest?.event_id && !recording && (
          <PitchTelemetryImporter
            captureId={latest.event_id}
            onAttached={() => loadLatest()}
          />
        )}
      </div>
    </div>
  );
}

/**
 * TelemetryPanel — compact ledger of all pitches linked to the most recent
 * capture (across all vendor sources). Shows a single rollup row per
 * ingestion + a click-to-expand pitch table.
 */
function TelemetryPanel({ telemetry }) {
  const [openIdx, setOpenIdx] = useState(0);
  if (!Array.isArray(telemetry) || telemetry.length === 0) return null;

  return (
    <div className="mt-5 pt-5 border-t border-white/10" data-testid="telemetry-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] inline-flex items-center gap-1.5">
          <Database className="h-3 w-3" /> Linked Vendor Telemetry · pitch-by-pitch
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/40">
          {telemetry.reduce((a, t) => a + (t.summary?.pitches || 0), 0)} pitches · {telemetry.length} source{telemetry.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="space-y-2">
        {telemetry.map((ing, i) => {
          const s = ing.summary || {};
          const open = openIdx === i;
          return (
            <div key={ing.ingestion_id || i} className="rounded-lg border border-white/10 bg-black/30 overflow-hidden" data-testid={`telemetry-ingestion-${i}`}>
              <button onClick={() => setOpenIdx(open ? -1 : i)}
                      className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded bg-[var(--brand-secondary)]/15 text-white">
                    {(s.source || ing.source || "?").toUpperCase()}
                  </span>
                  <span className="text-sm text-white font-semibold">{s.pitches || (ing.pitches?.length ?? 0)} pitches</span>
                </div>
                <div className="flex items-baseline gap-4 text-[11px] font-mono-num">
                  {s.max_velocity_mph != null && <span className="text-white"><span className="text-white/40">peak</span> {s.max_velocity_mph} mph</span>}
                  {s.avg_velocity_mph != null && <span className="text-white/70"><span className="text-white/40">avg</span> {s.avg_velocity_mph}</span>}
                  {s.avg_spin_rpm != null && <span className="text-white/70"><span className="text-white/40">spin</span> {Math.round(s.avg_spin_rpm)}</span>}
                  {s.avg_extension_ft != null && <span className="text-white/70"><span className="text-white/40">ext</span> {s.avg_extension_ft}</span>}
                </div>
              </button>
              {open && Array.isArray(ing.pitches) && ing.pitches.length > 0 && (
                <div className="px-3 pb-3 overflow-x-auto">
                  <table className="w-full text-[11px] font-mono-num" data-testid={`telemetry-pitches-${i}`}>
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-white/40">
                        <th className="text-left py-1.5">#</th>
                        <th className="text-left py-1.5">Type</th>
                        <th className="text-right py-1.5">Velo</th>
                        <th className="text-right py-1.5">Spin</th>
                        <th className="text-right py-1.5">IVB</th>
                        <th className="text-right py-1.5">HB</th>
                        <th className="text-right py-1.5">Ext</th>
                        <th className="text-right py-1.5">Rel H</th>
                        <th className="text-right py-1.5">Tilt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ing.pitches.slice(0, 50).map((p, j) => (
                        <tr key={j} className="border-t border-white/5 text-white/85">
                          <td className="py-1 text-white/40">{j + 1}</td>
                          <td className="py-1">{p.pitch_type || "—"}</td>
                          <td className="py-1 text-right">{fmt(p.velocity_mph, 1)}</td>
                          <td className="py-1 text-right">{fmt(p.spin_rpm, 0)}</td>
                          <td className="py-1 text-right">{fmt(p.induced_vert_break_in, 1)}</td>
                          <td className="py-1 text-right">{fmt(p.horz_break_in, 1)}</td>
                          <td className="py-1 text-right">{fmt(p.extension_ft, 1)}</td>
                          <td className="py-1 text-right">{fmt(p.release_height_ft, 1)}</td>
                          <td className="py-1 text-right">{p.tilt_clock || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {ing.pitches.length > 50 && (
                    <div className="text-[10px] text-white/40 mt-2 text-center">
                      Showing first 50 of {ing.pitches.length} pitches.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmt(v, decimals = 1) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}
